<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Button, IconButton, Modal, Token } from '@Shelly/shelly-design-system/index';
	import {
		type QuickControlEntry,
		predefinedEntries,
		getCustomTimer,
		deleteCustomTimer,
	} from 'src/helpers/quick_control_helper';
	import { formatTimeLabel } from 'src/helpers/timer_helpers';
	import { createEventDispatcher, onMount } from 'svelte';
	import AddEditCustomTimerModal from './AddEditCustomTimerModal.svelte';
	import { get } from 'svelte/store';
	import { devices } from 'src/stores/lists/devices';

	export let isOn = false;
	export let icon = 'far fa-clock';
	export let showQuickControlModal = false;
	export let deviceId: string = '';

	let showAddModal = false;
	let isEditMode = false;
	const entries = predefinedEntries() || ([] as QuickControlEntry[]);
	const dispatch = createEventDispatcher();

	let selectedTime: QuickControlEntry = { hr: '00', min: '00', sec: '00' };
	let customTimer: QuickControlEntry | null = null;
	let isEditingTimer: boolean = false;
	let isShared: boolean = false;

	onMount(() => {
		if (deviceId) {
			loadCustomTimer();
		}
		isShared = get(devices)?.[deviceId]?.shared;
	});

	const loadCustomTimer = () => {
		const savedTimer = getCustomTimer(deviceId);
		if (savedTimer) {
			customTimer = savedTimer;
		} else {
			customTimer = null;
		}
	};

	const handleSaveCustomTimer = () => {
		loadCustomTimer();
		isEditMode = false;
		isEditingTimer = false;
	};

	const handleDeleteCustomTimer = async () => {
		await deleteCustomTimer(deviceId);
		customTimer = null;
		isEditMode = false;
		isEditingTimer = false;
		showAddModal = false;
	};

	const toggleEditMode = () => {
		isEditMode = !isEditMode;
	};

	const handleAddButtonClick = () => {
		if (customTimer) {
			toggleEditMode();
		} else {
			isEditingTimer = false;
			selectedTime = { hr: '00', min: '00', sec: '00' };
			showAddModal = true;
		}
	};

	const handleCustomTimerClick = () => {
		if (isEditMode) {
			// Open modal for editing with pre-filled values
			isEditingTimer = true;
			selectedTime = {
				hr: customTimer?.hr || '00',
				min: customTimer?.min || '00',
				sec: customTimer?.sec || '00',
			};
			showAddModal = true;
		} else {
			dispatch('select', customTimer);
		}
	};

	$: taglineQuickControl = isOn
		? $format('title_quick_control_modal_turn_off', {
				default: 'Turn the device off and turn it on after',
			})
		: $format('title_quick_control_modal_turn_on', {
				default: 'Turn the device on and turn it off after',
			});
</script>

<Modal
	on:close={() => {
		showQuickControlModal = false;
		isEditMode = false;
	}}
	showModal={showQuickControlModal}
	title={taglineQuickControl}
>
	<div slot="content" class="">
		{#if entries.length > 0}
			{#each entries as entry}
				<Token
					id="token-1dqksc7"
					size="m"
					title={formatTimeLabel('00', entry.min, entry.sec, '00')}
					class="mb-s mr-s"
					bgColor="--color-2"
					selected={false}
					icon={entry.icon || icon}
					on:click={() => dispatch('select', entry)}
				/>
			{/each}
		{/if}

		{#if customTimer}
			<hr class="mt-m mb-m" />
			<Token
				id="token-custom"
				size="m"
				title={formatTimeLabel(customTimer.hr || '00', customTimer.min, customTimer.sec, '00')}
				class="mb-s mr-xs ml-0"
				bgColor={isEditMode ? '--color-1' : '--color-2'}
				selected={false}
				icon={isEditMode ? 'fas fa-pen' : customTimer.icon || icon}
				on:click={handleCustomTimerClick}
			/>
		{/if}

		{#if !isShared}
			<IconButton
				class="ml-0 mr-xs mb-s"
				shape="circle"
				icon={customTimer ? (isEditMode ? 'fas fa-times' : 'fas fa-pen') : 'fas fa-plus'}
				on:click={handleAddButtonClick}
				aria-label={customTimer
					? isEditMode
						? $format('button_cancel', { default: 'Cancel' })
						: $format('button_edit', { default: 'Edit' })
					: $format('button_add_timer', { default: 'Add timer' })}
			/>
		{/if}
	</div>
	<div slot="footer" class="d-flex justify-content-end">
		<Button
			variant="secondary"
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			on:click={() => {
				showQuickControlModal = false;
				isEditMode = false;
			}}
		>
			{$format(`button_cancel`, { default: 'Cancel' })}
		</Button>
	</div>
</Modal>

<AddEditCustomTimerModal
	bind:showAddModal
	bind:selectedTime
	{deviceId}
	isEditMode={isEditingTimer}
	on:save={handleSaveCustomTimer}
	on:delete={handleDeleteCustomTimer}
/>
